<script setup>
import { reactive, computed, ref, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { minLength, requiredIf, url } from '@vuelidate/validators';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';

const props = defineProps({
  assistantId: {
    type: Number,
    required: true,
  },
});

const emit = defineEmits(['submit', 'cancel']);

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

const { t } = useI18n();

const formState = {
  uiFlags: useMapGetter('captainDocuments/getUIFlags'),
};

const initialState = {
  name: '',
  url: '',
  documentType: 'url',
  sourceFile: null,
  content: '',
};

const state = reactive({ ...initialState });
const fileInputRef = ref(null);

const validationRules = {
  url: {
    required: requiredIf(() => state.documentType === 'url'),
    url: requiredIf(() => state.documentType === 'url' && url),
    minLength: requiredIf(() => state.documentType === 'url' && minLength(1)),
  },
  sourceFile: {
    required: requiredIf(() => state.documentType === 'file'),
  },
  content: {
    required: requiredIf(() => state.documentType === 'text'),
    minLength: requiredIf(
      () => state.documentType === 'text' && minLength(1)
    ),
  },
};

const documentTypeOptions = [
  { value: 'url', label: t('CAPTAIN.DOCUMENTS.FORM.TYPE.URL') },
  { value: 'file', label: t('CAPTAIN.DOCUMENTS.FORM.TYPE.FILE') },
  { value: 'text', label: t('CAPTAIN.DOCUMENTS.FORM.TYPE.TEXT') },
];

const v$ = useVuelidate(validationRules, state);

const isLoading = computed(() => formState.uiFlags.value.creatingItem);

const hasSourceFileError = computed(() => v$.value.sourceFile.$error);

const getErrorMessage = (field, errorKey) => {
  return v$.value[field].$error
    ? t(`CAPTAIN.DOCUMENTS.FORM.${errorKey}.ERROR`)
    : '';
};

const formErrors = computed(() => ({
  url: getErrorMessage('url', 'URL'),
  sourceFile: getErrorMessage('sourceFile', 'SOURCE_FILE'),
  content: getErrorMessage('content', 'CONTENT'),
}));

const handleCancel = () => emit('cancel');

const handleFileChange = event => {
  const file = event.target.files[0];
  if (file) {
    const allowedExtensions = ['.txt', '.md', '.json'];
    const fileName = file.name.toLowerCase();
    const hasAllowedExtension = allowedExtensions.some(extension =>
      fileName.endsWith(extension)
    );

    if (!hasAllowedExtension) {
      useAlert(t('CAPTAIN.DOCUMENTS.FORM.SOURCE_FILE.INVALID_TYPE'));
      event.target.value = '';
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      useAlert(t('CAPTAIN.DOCUMENTS.FORM.SOURCE_FILE.TOO_LARGE'));
      event.target.value = '';
      return;
    }
    state.sourceFile = file;
    state.name = file.name.replace(/\.(txt|md|json)$/i, '');
  }
};

const openFileDialog = () => {
  // Use nextTick to ensure the ref is available
  nextTick(() => {
    if (fileInputRef.value) {
      fileInputRef.value.click();
    }
  });
};

const prepareDocumentDetails = () => {
  const formData = new FormData();
  formData.append('document[assistant_id]', props.assistantId);

  if (state.documentType === 'url') {
    formData.append('document[external_link]', state.url);
    formData.append('document[name]', state.name || state.url);
  } else if (state.documentType === 'text') {
    formData.append('document[content]', state.content);
    formData.append('document[name]', state.name);
  } else {
    formData.append('document[source_file]', state.sourceFile);
    formData.append(
      'document[name]',
      state.name || state.sourceFile.name.replace(/\.(txt|md|json)$/i, '')
    );
    // No need to send external_link for uploaded files - it's auto-generated in the backend.
  }

  return formData;
};

const handleSubmit = async () => {
  const isFormValid = await v$.value.$validate();
  if (!isFormValid) {
    return;
  }

  emit('submit', prepareDocumentDetails());
};
</script>

<template>
  <form class="flex flex-col gap-4" @submit.prevent="handleSubmit">
    <div class="flex flex-col gap-1">
      <label
        for="documentType"
        class="mb-0.5 text-sm font-medium text-n-slate-12"
      >
        {{ t('CAPTAIN.DOCUMENTS.FORM.TYPE.LABEL') }}
      </label>
      <ComboBox
        id="documentType"
        v-model="state.documentType"
        :options="documentTypeOptions"
        class="[&>div>button]:bg-n-alpha-black2"
      />
    </div>

    <Input
      v-if="state.documentType === 'url'"
      v-model="state.url"
      :label="t('CAPTAIN.DOCUMENTS.FORM.URL.LABEL')"
      :placeholder="t('CAPTAIN.DOCUMENTS.FORM.URL.PLACEHOLDER')"
      :message="formErrors.url"
      :message-type="formErrors.url ? 'error' : 'info'"
    />

    <div v-if="state.documentType === 'text'" class="flex flex-col gap-2">
      <TextArea
        id="documentContent"
        v-model="state.content"
        :label="t('CAPTAIN.DOCUMENTS.FORM.CONTENT.LABEL')"
        :placeholder="t('CAPTAIN.DOCUMENTS.FORM.CONTENT.PLACEHOLDER')"
        :message="formErrors.content"
        :message-type="formErrors.content ? 'error' : 'info'"
        :max-length="200000"
        :show-character-count="true"
        auto-height
        min-height="12rem"
        max-height="12rem"
        custom-text-area-class="font-mono"
      />
      <p class="m-0 text-xs text-n-slate-11">
        {{ t('CAPTAIN.DOCUMENTS.FORM.CONTENT.HELP_TEXT') }}
      </p>
    </div>

    <div v-if="state.documentType === 'file'" class="flex flex-col gap-2">
      <label class="text-sm font-medium text-n-slate-12">
        {{ t('CAPTAIN.DOCUMENTS.FORM.SOURCE_FILE.LABEL') }}
      </label>
      <div class="relative">
        <input
          ref="fileInputRef"
          type="file"
          accept=".txt,.md,.json"
          class="hidden"
          @change="handleFileChange"
        />
        <Button
          type="button"
          :color="hasSourceFileError ? 'ruby' : 'slate'"
          :variant="hasSourceFileError ? 'outline' : 'solid'"
          class="!w-full !h-auto !justify-between !py-4"
          @click="openFileDialog"
        >
          <template #default>
            <div class="flex gap-2 items-center">
              <div
                class="flex justify-center items-center w-10 h-10 rounded-lg bg-n-slate-3"
              >
                <i class="text-xl i-ph-file-text text-n-slate-11" />
              </div>
              <div class="flex flex-col flex-1 gap-1 items-start">
                <p class="m-0 text-sm font-medium text-n-slate-12">
                  {{
                    state.sourceFile
                      ? state.sourceFile.name
                      : t('CAPTAIN.DOCUMENTS.FORM.SOURCE_FILE.CHOOSE_FILE')
                  }}
                </p>
                <p class="m-0 text-xs text-n-slate-11">
                  {{
                    state.sourceFile
                      ? `${(state.sourceFile.size / 1024 / 1024).toFixed(2)} MB`
                      : t('CAPTAIN.DOCUMENTS.FORM.SOURCE_FILE.HELP_TEXT')
                  }}
                </p>
              </div>
            </div>

            <i class="i-lucide-upload text-n-slate-11" />
          </template>
        </Button>
      </div>
      <p v-if="formErrors.sourceFile" class="text-xs text-n-ruby-9">
        {{ formErrors.sourceFile }}
      </p>
    </div>

    <Input
      v-model="state.name"
      :label="t('CAPTAIN.DOCUMENTS.FORM.NAME.LABEL')"
      :placeholder="t('CAPTAIN.DOCUMENTS.FORM.NAME.PLACEHOLDER')"
    />

    <div class="flex gap-3 justify-between items-center w-full">
      <Button
        type="button"
        variant="faded"
        color="slate"
        :label="t('CAPTAIN.FORM.CANCEL')"
        class="w-full bg-n-alpha-2 text-n-blue-11 hover:bg-n-alpha-3"
        @click="handleCancel"
      />
      <Button
        type="submit"
        :label="t('CAPTAIN.FORM.CREATE')"
        class="w-full"
        :is-loading="isLoading"
        :disabled="isLoading"
      />
    </div>
  </form>
</template>
